
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Mail, Lock, ShieldCheck, AlertTriangle, CheckCircle2, User, Eye, EyeOff, KeyRound, ShieldAlert } from 'lucide-react';
import { sendOtp, signInWithGoogle, signInWithPassword, signUpWithPassword, verifyOtp } from '../services/backendService';
import { User as AppUser, AppView } from '../types';
import { Logo } from './Logo';

interface AuthFormProps {
  onAuthSuccess: (user: AppUser) => void;
  isOpen: boolean;
  onClose?: () => void;
  onNavigate?: (view: AppView) => void;
}

const AuthForm: React.FC<AuthFormProps> = ({ onAuthSuccess, isOpen, onClose, onNavigate }) => {
  const [method, setMethod] = useState<'menu' | 'email_input' | 'otp_input' | 'password' | 'check_email_link'>('menu');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [otpToken, setOtpToken] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleGoogleLogin = async () => {
    if (!agreed) {
      setError("Please check the box to agree to our Terms and Privacy Policy.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError('Google Sign-In failed.');
      setLoading(false);
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      setError("Please check the box to agree to our Terms and Privacy Policy.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      await sendOtp(email);
      setMethod('otp_input');
    } catch (err: any) {
      setError(err.message || 'Failed to send link.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpToken.trim()) return;
    setLoading(true);
    setError('');
    try {
      const user = await verifyOtp(email, otpToken);
      onAuthSuccess(user);
    } catch (err: any) {
      setError('Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      setError("Please check the box to agree to our Terms and Privacy Policy.");
      return;
    }
    setLoading(true);
    setError('');

    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      setLoading(false);
      return;
    }

    try {
      let user;
      if (isSignUp) {
        if (!name.trim()) throw new Error("Name is required.");

        try {
          user = await signUpWithPassword(email, password, name);
        } catch (signupError: any) {
          if (signupError.message === 'CONFIRM_EMAIL') {
            // Show the check email screen
            setMethod('otp_input');
            // Temporarily change the title of OTP input to just "Check Email" or reuse it
            // Actually, otp_input expects an OTP. The user just needs to click the link.
            // Let's create a new 'check_email' view or reuse/hack 'otp_input' to just show a message.
            // For simplicity and reusing the UI:
            setMethod('check_email_link'); // We'll add this view state
            return;
          }
          throw signupError;
        }

      } else {
        user = await signInWithPassword(email, password);
      }
      onAuthSuccess(user);
    } catch (err: any) {
      setError(err.message || 'Authentication failed.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMethod('menu');
    setError('');
    setIsSignUp(false);
    setOtpToken('');
  };

  const inputClasses = "w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-sage-500 focus:border-transparent outline-none transition-all text-gray-800 placeholder:text-gray-400 shadow-sm";
  const labelStyle = "text-[9px] md:text-[10px] font-bold uppercase tracking-widest font-sans select-none leading-relaxed";

  const CheckboxUI = () => (
    <div className="pt-2 px-1">
      <label className="flex items-start gap-3 cursor-pointer group">
        <div className="relative flex items-center mt-0.5">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 bg-white checked:bg-[#4A7C59] checked:border-[#4A7C59] transition-all"
          />
          <CheckCircle2 className="absolute h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity left-0.5" />
        </div>
        <span className={`${labelStyle} text-gray-500 uppercase`}>
          I AGREE TO THE <Link to="/terms" target="_blank" className="text-[#3B6EB1] hover:underline font-black">TERMS</Link> AND <Link to="/privacy" target="_blank" className="text-[#3B6EB1] hover:underline font-black">PRIVACY POLICY</Link>
        </span>
      </label>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-sage-900/40 backdrop-blur-md p-4 animate-in fade-in duration-300">
      <div className="bg-[#FFFCF7] rounded-[2.5rem] shadow-2xl max-w-md w-full overflow-hidden flex flex-col relative border border-white/40">

        <div className="p-10 pb-6 text-center">
          <div className="flex justify-center mb-6">
            <Logo className="h-16 w-16" textClassName="text-4xl" showText={false} />
          </div>
          <h2 className="text-4xl font-serif font-bold text-[#8B0000] tracking-tight mb-1">
            Nature Nani
          </h2>
          <p className="text-sage-600 text-[10px] font-bold uppercase tracking-widest font-sans">
            Find the cure in Nature
          </p>
        </div>

        <div className="px-10 pb-6">
          {error && (
            <div className="mb-6 bg-red-50 text-red-700 text-xs p-3.5 rounded-xl border border-red-100 flex items-center gap-2 animate-in shake duration-300">
              <AlertTriangle size={14} className="flex-shrink-0" /> {error}
            </div>
          )}

          {method === 'menu' && (
            <div className="space-y-4">
              <button onClick={handleGoogleLogin} disabled={loading} className="w-full bg-white text-gray-700 border border-gray-200 py-4 rounded-xl font-bold hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-3 shadow-sm active:scale-[0.98]">
                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                Continue with Google
              </button>

              <button onClick={() => { setMethod('password'); setIsSignUp(true); }} className="w-full bg-white text-[#8B0000] border-2 border-[#8B0000] py-4 rounded-xl font-bold hover:bg-[#8B0000]/5 transition-all flex items-center justify-center gap-3 shadow-sm active:scale-[0.98]">
                <Mail size={20} />
                Sign up with email
              </button>

              <button onClick={() => setMethod('email_input')} className="w-full bg-[#4A7C59] text-white py-4 rounded-xl font-bold hover:bg-[#3d664a] transition-all flex items-center justify-center gap-2 shadow-lg shadow-sage-200/50 active:scale-[0.98]">
                <KeyRound size={20} />
                Send One time link
              </button>

              <CheckboxUI />

              <div className="pt-6 text-center space-y-4">
                <p className={`${labelStyle} text-gray-400`}>
                  WHAT DOES THIS APP DO? <button onClick={() => onNavigate?.(AppView.ABOUT)} className="text-[#3B6EB1] font-black hover:underline ml-1">LEARN MORE</button>
                </p>
                <p className={`${labelStyle} text-gray-400`}>
                  HAVE AN ACCOUNT? <button onClick={() => { setMethod('password'); setIsSignUp(false); }} className="text-[#3B6EB1] font-black hover:underline ml-1">LOG IN</button>
                </p>
              </div>
            </div>
          )}

          {method === 'password' && (
            <form onSubmit={handlePasswordAuth} className="space-y-4 animate-in slide-in-from-bottom-4 duration-300">
              {isSignUp && (
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className={inputClasses} placeholder="Full Name" />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClasses} placeholder="Email Address" />
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputClasses} pr-12`} placeholder="Password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-sage-600 transition-colors">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <CheckboxUI />
              <button type="submit" disabled={loading} className="w-full bg-[#4A7C59] text-white py-4 rounded-xl font-bold hover:bg-[#3d664a] transition-all flex items-center justify-center gap-2 shadow-lg">
                {loading ? <Loader2 className="animate-spin" /> : <>{isSignUp ? 'Sign Up' : 'Log In'} <ArrowRight size={18} /></>}
              </button>
              <button type="button" onClick={reset} className={`${labelStyle} text-gray-400 hover:text-gray-600 py-2 w-full text-center transition-colors`}>Back to options</button>
            </form>
          )}

          {method === 'email_input' && (
            <form onSubmit={handleSendOtp} className="space-y-5 animate-in slide-in-from-bottom-4 duration-300">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className={inputClasses} placeholder="name@email.com" />
              </div>
              <CheckboxUI />
              <button type="submit" disabled={loading} className="w-full bg-[#4A7C59] text-white py-4 rounded-xl font-bold hover:bg-[#3d664a] transition-all flex items-center justify-center gap-2 shadow-lg shadow-sage-200/50">
                {loading ? <Loader2 className="animate-spin" /> : <>Send One time link <ArrowRight size={18} /></>}
              </button>
              <button type="button" onClick={reset} className={`${labelStyle} text-gray-400 hover:text-gray-600 py-2 w-full text-center transition-colors`}>Back to options</button>
            </form>
          )}

          {method === 'otp_input' && (
            <form onSubmit={handleVerifyOtp} className="text-center py-6 animate-in slide-in-from-bottom-4 duration-300 space-y-6">
              <div className="w-16 h-16 bg-sage-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-sage-100">
                <Mail className="text-[#4A7C59]" size={32} />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">Check your email</p>
                <p className="text-xs text-gray-500 mt-1">Enter the 6-digit code sent to {email}</p>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  maxLength={6}
                  required
                  autoFocus
                  value={otpToken}
                  onChange={(e) => setOtpToken(e.target.value.replace(/\D/g, ''))}
                  className={`${inputClasses} tracking-[1em] font-black text-center pl-4`}
                  placeholder="000000"
                />
              </div>
              <button type="submit" disabled={loading || otpToken.length < 6} className="w-full bg-[#4A7C59] text-white py-4 rounded-xl font-bold hover:bg-[#3d664a] transition-all flex items-center justify-center gap-2 shadow-lg shadow-sage-200/50 disabled:opacity-50">
                {loading ? <Loader2 className="animate-spin" /> : <>Verify & Sign In <ArrowRight size={18} /></>}
              </button>
              <button type="button" onClick={reset} className={`${labelStyle} text-[#3B6EB1] hover:underline mt-6 w-full`}>Back to options</button>
            </form>
          )}

          {method === 'check_email_link' && (
            <div className="text-center py-6 animate-in slide-in-from-bottom-4 duration-300 space-y-6">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-100">
                <Mail className="text-[#3B6EB1]" size={32} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Verify your email</h3>
                <p className="text-sm text-gray-500 max-w-[260px] mx-auto">
                  We've sent a verification link to <span className="font-bold text-gray-700">{email}</span>.
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Please click the link in that email to verify your account and log in.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setMethod('password'); setIsSignUp(false); }}
                className="w-full bg-[#3B6EB1] text-white py-4 rounded-xl font-bold hover:bg-[#2c538a] transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-200/50"
              >
                Back to Login <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="bg-white/50 p-8 border-t border-gray-100 flex items-center justify-center gap-16">
          <Link to="/disclaimer" target="_blank" className="flex flex-col items-center gap-1 group">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 group-hover:bg-[#8B0000]/10 group-hover:text-[#8B0000] transition-all shadow-sm">
              <ShieldAlert size={20} />
            </div>
            <span className={`${labelStyle} text-gray-400 group-hover:text-gray-600`}>Disclaimer</span>
          </Link>
          <Link to="/privacy" target="_blank" className="flex flex-col items-center gap-1 group">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 group-hover:bg-[#4A7C59]/10 group-hover:text-[#4A7C59] transition-all shadow-sm">
              <ShieldCheck size={20} />
            </div>
            <span className={`${labelStyle} text-gray-400 group-hover:text-gray-600`}>Privacy</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
